/** Verificar uma entrega: o que o termo (ou o contentor ASiC) diz, quem
 *  o assinou, se o conteúdo assinado está intacto, e se os ficheiros
 *  recebidos são exactamente os que lá estão.
 *
 *  O que se verifica aqui: a integridade (o resumo guardado dentro da
 *  assinatura bate com o resumo do conteúdo) e a correspondência dos
 *  ficheiros. O que NÃO se verifica: a assinatura criptográfica e a
 *  cadeia de confiança do certificado (se é de facto um certificado
 *  qualificado, válido e não revogado). Isso faz-se no Autenticação.gov
 *  ou no validador da Comissão Europeia; a interface di-lo sempre. */

import { unzipSync } from 'fflate'

import { lerAssinaturaCms, lerCertificado, lerNo, hex, type AssinaturaCms, type CertificadoX509, RESUMOS } from './der'
import { MARCA_FIM, MARCA_INICIO, type FicheiroEntregue, type ManifestoDeEntrega } from './termoDeEntrega'

function latin1(b: Uint8Array): string {
  let s = ''
  const passo = 0x8000
  for (let i = 0; i < b.length; i += passo) s += String.fromCharCode(...b.subarray(i, i + passo))
  return s
}

export async function resumoDe(alg: 'SHA-1' | 'SHA-256' | 'SHA-384' | 'SHA-512', dados: Uint8Array | ArrayBuffer): Promise<string> {
  const buf = dados instanceof Uint8Array ? dados.slice().buffer : dados
  return hex(new Uint8Array(await crypto.subtle.digest(alg, buf as ArrayBuffer)))
}

export function sha256Hex(dados: Uint8Array | ArrayBuffer): Promise<string> {
  return resumoDe('SHA-256', dados)
}

// ---------------------------------------------------------------------
// O termo em PDF
// ---------------------------------------------------------------------

/** O manifesto escrito pelo hub no termo, se lá estiver. Devolve também
 *  onde começa, para se saber se ficou dentro da parte assinada. */
export function extrairManifesto(pdf: Uint8Array): { manifesto: ManifestoDeEntrega; posicao: number } | null {
  const t = latin1(pdf)
  const i = t.indexOf(MARCA_INICIO)
  if (i < 0) return null
  const j = t.indexOf(MARCA_FIM, i)
  if (j < 0) return null
  try {
    const m = JSON.parse(t.slice(i + MARCA_INICIO.length, j).trim()) as ManifestoDeEntrega
    if (!Array.isArray(m.ficheiros)) return null
    return { manifesto: m, posicao: i }
  } catch {
    return null
  }
}

export interface AssinaturaPdf {
  /** Ordem no ficheiro (1 = a primeira). */
  ordem: number
  byteRange: [number, number, number, number]
  /** /SubFilter: ETSI.CAdES.detached (PAdES), adbe.pkcs7.detached, ... */
  formato: string | null
  cms: AssinaturaCms | null
  /** Erro ao ler a assinatura (formato que não sabemos ler). */
  erro: string | null
  /** O resumo do conteúdo assinado bate com o que a assinatura guarda. */
  integra: boolean | null
  /** A assinatura cobre o ficheiro até ao fim (nada acrescentado depois). */
  cobreAteAoFim: boolean
  /** Bytes acrescentados depois desta assinatura. */
  bytesDepois: number
}

/** Todas as assinaturas de um PDF, com a verificação de integridade de
 *  cada uma (o messageDigest da CMS contra o resumo do ByteRange). */
export async function assinaturasDoPdf(pdf: Uint8Array): Promise<AssinaturaPdf[]> {
  const t = latin1(pdf)
  const out: AssinaturaPdf[] = []
  const re = /\/ByteRange\s*\[\s*(\d+)\s+(\d+)\s+(\d+)\s+(\d+)\s*\]/g
  let m: RegExpExecArray | null
  let ordem = 0
  while ((m = re.exec(t))) {
    const br = [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4])] as [number, number, number, number]
    const [o1, l1, o2, l2] = br
    if (o1 !== 0 || o1 + l1 > o2 || o2 + l2 > pdf.length) continue
    ordem++
    // A /Contents é exactamente o buraco entre os dois intervalos: <hex>.
    const buraco = t.slice(o1 + l1, o2).trim()
    const hexa = buraco.replace(/^</, '').replace(/>$/, '').replace(/\s+/g, '')
    // O dicionário da assinatura está à volta do buraco.
    const janela = t.slice(Math.max(0, o1 + l1 - 4000), Math.min(t.length, o2 + 4000))
    const formato = /\/SubFilter\s*\/([\w.]+)/.exec(janela)?.[1] ?? null
    let cms: AssinaturaCms | null = null
    let erro: string | null = null
    let integra: boolean | null = null
    try {
      const bytes = new Uint8Array(hexa.length / 2)
      for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(hexa.substr(i * 2, 2), 16)
      // O enchimento a zeros fica de fora: o DER diz o comprimento.
      const n = lerNo(bytes, 0)
      cms = lerAssinaturaCms(bytes.subarray(0, n.seguinte))
      if (cms.resumo && cms.resumoAssinado) {
        const conteudo = new Uint8Array(l1 + l2)
        conteudo.set(pdf.subarray(o1, o1 + l1), 0)
        conteudo.set(pdf.subarray(o2, o2 + l2), l1)
        integra = (await resumoDe(cms.resumo, conteudo)) === cms.resumoAssinado
      }
    } catch (e) {
      erro = e instanceof Error ? e.message : String(e)
    }
    out.push({
      ordem,
      byteRange: br,
      formato,
      cms,
      erro,
      integra,
      cobreAteAoFim: o2 + l2 === pdf.length,
      bytesDepois: pdf.length - (o2 + l2),
    })
  }
  return out
}

// ---------------------------------------------------------------------
// Contentor ASiC (ASiC-E ou ASiC-S)
// ---------------------------------------------------------------------

export interface ReferenciaAssinada {
  nome: string
  algoritmo: 'SHA-1' | 'SHA-256' | 'SHA-384' | 'SHA-512' | null
  resumo: string
}

export interface AssinaturaNoContentor {
  ficheiro: string
  formato: 'CAdES' | 'XAdES'
  /** CAdES: a CMS lida; XAdES: null. */
  cms: AssinaturaCms | null
  /** O certificado do assinante (CN do sujeito e do emissor, validade). */
  certificado: CertificadoX509 | null
  /** Data declarada na assinatura (signingTime ou SigningTime). */
  data: string | null
  erro: string | null
  /** CAdES: o resumo assinado bate com o do ASiCManifest (ASiC-E) ou com
   *  o do ficheiro (ASiC-S). XAdES: todos os ficheiros que a assinatura
   *  refere conferem com o resumo que ela declara. Null: não se sabe. */
  integra: boolean | null
}

export interface ContentorAsic {
  tipo: string | null
  /** Os ficheiros de dados (fora de META-INF e do mimetype). */
  ficheiros: Array<FicheiroEntregue & { bytes: Uint8Array }>
  assinaturas: AssinaturaNoContentor[]
  /** O que as assinaturas (ou o manifesto ASiC) dizem de cada ficheiro. */
  referencias: ReferenciaAssinada[]
}

const ALGORITMOS_XML: Record<string, ReferenciaAssinada['algoritmo']> = {
  'http://www.w3.org/2000/09/xmldsig#sha1': 'SHA-1',
  'http://www.w3.org/2001/04/xmlenc#sha256': 'SHA-256',
  'http://www.w3.org/2001/04/xmldsig-more#sha384': 'SHA-384',
  'http://www.w3.org/2001/04/xmlenc#sha512': 'SHA-512',
}

function base64ParaHex(b64: string): string {
  const s = atob(b64.replace(/\s+/g, ''))
  let h = ''
  for (let i = 0; i < s.length; i++) h += s.charCodeAt(i).toString(16).padStart(2, '0')
  return h
}

/** Referências (URI, algoritmo, resumo) de um XML de assinatura XAdES ou
 *  de um ASiCManifest. Lê por expressões regulares, sem DOM, para correr
 *  também nos testes em Node. */
export function referenciasDoXml(xml: string): ReferenciaAssinada[] {
  const out: ReferenciaAssinada[] = []
  const re = /<(?:\w+:)?(Reference|DataObjectReference)\b([^>]*)>([\s\S]*?)<\/(?:\w+:)?\1>/g
  let m: RegExpExecArray | null
  while ((m = re.exec(xml))) {
    const uri = /URI="([^"]*)"/.exec(m[2])?.[1]
    if (!uri || uri.startsWith('#')) continue
    const alg = /DigestMethod\b[^>]*Algorithm="([^"]+)"/.exec(m[3])?.[1] ?? ''
    const valor = /<(?:\w+:)?DigestValue>([^<]+)</.exec(m[3])?.[1]
    if (!valor) continue
    out.push({ nome: decodeURIComponent(uri), algoritmo: ALGORITMOS_XML[alg] ?? null, resumo: base64ParaHex(valor) })
  }
  return out
}

function certificadoDoXml(xml: string): Uint8Array | null {
  const b64 = /<(?:\w+:)?X509Certificate>([^<]+)</.exec(xml)?.[1]
  if (!b64) return null
  const s = atob(b64.replace(/\s+/g, ''))
  const b = new Uint8Array(s.length)
  for (let i = 0; i < s.length; i++) b[i] = s.charCodeAt(i)
  return b
}

async function referenciasConferem(
  refs: readonly ReferenciaAssinada[],
  ficheiros: ContentorAsic['ficheiros'],
): Promise<boolean | null> {
  if (refs.length === 0) return null
  for (const r of refs) {
    const f = ficheiros.find((x) => x.nome === r.nome || r.nome.endsWith('/' + x.nome))
    if (!f || !r.algoritmo || (await resumoDe(r.algoritmo, f.bytes)) !== r.resumo) return false
  }
  return true
}

export async function lerContentorAsic(zip: Uint8Array): Promise<ContentorAsic> {
  const ficheiros = unzipSync(zip)
  const tipo = ficheiros.mimetype ? new TextDecoder().decode(ficheiros.mimetype).trim() : null
  const dados: ContentorAsic['ficheiros'] = []
  for (const [nome, bytes] of Object.entries(ficheiros)) {
    if (nome === 'mimetype' || nome.startsWith('META-INF/') || nome.endsWith('/')) continue
    dados.push({ nome, tamanho: bytes.length, sha256: await sha256Hex(bytes), bytes })
  }
  const assinaturas: AssinaturaNoContentor[] = []
  const referencias: ReferenciaAssinada[] = []
  for (const [nome, bytes] of Object.entries(ficheiros)) {
    if (!nome.startsWith('META-INF/')) continue
    const base = nome.slice('META-INF/'.length).toLowerCase()
    if (base.endsWith('.p7s')) {
      try {
        const cms = lerAssinaturaCms(bytes)
        // O que a CAdES assina: o ASiCManifest (ASiC-E) ou o ficheiro
        // único (ASiC-S).
        const candidatos: Uint8Array[] = Object.entries(ficheiros)
          .filter(([n]) => /^META-INF\/ASiCManifest[^/]*\.xml$/i.test(n))
          .map(([, b]) => b)
        if (candidatos.length === 0 && dados.length === 1) candidatos.push(dados[0].bytes)
        let integra: boolean | null = null
        if (cms.resumo && cms.resumoAssinado && candidatos.length > 0) {
          integra = false
          for (const b of candidatos) if ((await resumoDe(cms.resumo, b)) === cms.resumoAssinado) integra = true
        }
        assinaturas.push({
          ficheiro: nome,
          formato: 'CAdES',
          cms,
          certificado: { sujeito: cms.assinante, emissor: cms.emissor, validoDe: cms.certificadoValidoDe, validoAte: cms.certificadoValidoAte },
          data: cms.dataDeclarada,
          erro: null,
          integra,
        })
      } catch (e) {
        assinaturas.push({ ficheiro: nome, formato: 'CAdES', cms: null, certificado: null, data: null, erro: e instanceof Error ? e.message : String(e), integra: null })
      }
    } else if (base.endsWith('.xml')) {
      const xml = new TextDecoder().decode(bytes)
      const refs = referenciasDoXml(xml)
      referencias.push(...refs)
      if (base.includes('signature')) {
        // XAdES (o Autenticação.gov escreve META-INF/signatures001.xml).
        // O primeiro X509Certificate é o do assinante (KeyInfo); os da
        // validação de longo prazo vêm depois, noutros elementos.
        let certificado: CertificadoX509 | null = null
        let erro: string | null = null
        try {
          const der = certificadoDoXml(xml)
          certificado = der ? lerCertificado(der) : null
        } catch (e) {
          erro = e instanceof Error ? e.message : String(e)
        }
        const dataXml = /<(?:\w+:)?SigningTime>([^<]+)</.exec(xml)?.[1] ?? null
        assinaturas.push({
          ficheiro: nome,
          formato: 'XAdES',
          cms: null,
          certificado,
          data: dataXml,
          erro,
          integra: await referenciasConferem(refs, dados),
        })
      }
    }
  }
  return { tipo, ficheiros: dados, assinaturas, referencias }
}

/** Um termo gerado pelo hub dentro do contentor, se houver. */
export function termoNoContentor(c: ContentorAsic): { nome: string; manifesto: ManifestoDeEntrega } | null {
  for (const f of c.ficheiros) {
    if (!/\.pdf$/i.test(f.nome)) continue
    const m = extrairManifesto(f.bytes)
    if (m) return { nome: f.nome, manifesto: m.manifesto }
  }
  return null
}

/** Os ficheiros do contentor conferem com o que as assinaturas dizem? */
export async function conferirReferencias(c: ContentorAsic): Promise<Array<{ nome: string; estado: 'confere' | 'diferente' | 'nao-referido' }>> {
  const out: Array<{ nome: string; estado: 'confere' | 'diferente' | 'nao-referido' }> = []
  for (const f of c.ficheiros) {
    const refs = c.referencias.filter((r) => r.nome === f.nome || r.nome.endsWith('/' + f.nome))
    if (refs.length === 0) {
      out.push({ nome: f.nome, estado: 'nao-referido' })
      continue
    }
    let ok = true
    for (const r of refs) {
      if (!r.algoritmo || (await resumoDe(r.algoritmo, f.bytes)) !== r.resumo) ok = false
    }
    out.push({ nome: f.nome, estado: ok ? 'confere' : 'diferente' })
  }
  return out
}

// ---------------------------------------------------------------------
// Comparar com os ficheiros recebidos
// ---------------------------------------------------------------------

export type EstadoDoFicheiro = 'igual' | 'igual-outro-nome' | 'diferente' | 'em-falta'

export interface LinhaDaComparacao {
  esperado: FicheiroEntregue
  estado: EstadoDoFicheiro
  /** O ficheiro recebido que lhe corresponde, se houver. */
  recebido: { nome: string; sha256: string } | null
}

/** Compara a lista assinada com os ficheiros recebidos. Conta só a
 *  impressão digital: o nome serve para dizer "é este mas mudou". */
export function compararFicheiros(
  esperados: readonly FicheiroEntregue[],
  recebidos: ReadonlyArray<{ nome: string; sha256: string }>,
): { linhas: LinhaDaComparacao[]; aMais: Array<{ nome: string; sha256: string }> } {
  const usados = new Set<number>()
  const linhas = esperados.map((e): LinhaDaComparacao => {
    const sha = e.sha256.toLowerCase()
    let i = recebidos.findIndex((r, k) => !usados.has(k) && r.sha256.toLowerCase() === sha && r.nome === e.nome)
    if (i >= 0) {
      usados.add(i)
      return { esperado: e, estado: 'igual', recebido: recebidos[i] }
    }
    i = recebidos.findIndex((r, k) => !usados.has(k) && r.sha256.toLowerCase() === sha)
    if (i >= 0) {
      usados.add(i)
      return { esperado: e, estado: 'igual-outro-nome', recebido: recebidos[i] }
    }
    i = recebidos.findIndex((r, k) => !usados.has(k) && r.nome === e.nome)
    if (i >= 0) {
      usados.add(i)
      return { esperado: e, estado: 'diferente', recebido: recebidos[i] }
    }
    return { esperado: e, estado: 'em-falta', recebido: null }
  })
  return { linhas, aMais: recebidos.filter((_, k) => !usados.has(k)) }
}

export { RESUMOS }
